import { Formik } from "formik";
import {
  type QuotationRequestRQ,
  type QuotationRequestSessionType,
  type TripLeg,
  quotationRequestRQValidationSchema,
  createQuotationRequestRQ,
} from "./definitions";
import { Fragment } from "react/jsx-runtime";
import { QuotationRequestLoader } from "./QuotationRequestLoader";
import { InitialForm } from "./InitialForm";
import { QuotationDestination } from "./QuotationDestination";
import { QuotationTripLeg } from "./QuotationTripLeg";
import { BackButton } from "./componets";
import { brand } from "../../../context";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createQuotationV2,
  excludeService,
  pollQuotationRequest,
  reIncludeService,
} from "../../../api/quotation";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import _ from "lodash";
import { DateTime } from "luxon";
import { getLeanSubagents } from "../../../api/entity/subagents";
import { Loader } from "../../widgets/Loader";
import { sortByKey } from "../../../utils";

type QuotationRequestProps = {
  source_entity: string;
  target_entity: string;
  initialMessage: string;
  onBack: () => void;
  finalizeQuotationRequest: (session_id: string) => void;
};

export function QuotationRequestForm({
  source_entity,
  target_entity,
  initialMessage,
  finalizeQuotationRequest,
  onBack,
}: QuotationRequestProps) {
  const [sessionId, setSessionId] = useState<string>("");
  const [quotationSession, setQuotationSession] = useState<QuotationRequestSessionType | null>(
    null,
  );

  const tripStartDate = quotationSession?.quotation_request.trip_start_date;
  const adults = quotationSession?.quotation_request.adults;
  const children = quotationSession?.quotation_request.children;
  const nights = (quotationSession?.quotation_request?.destinations ?? []).reduce((acc, dest) => {
    return acc + dest.stay;
  }, 0);
  let endDate = "";
  if (!!tripStartDate) {
    endDate = DateTime.fromISO(tripStartDate)
      .plus({ days: nights })
      .toLocaleString(DateTime.DATE_FULL);
  }

  const { data: subagents, isFetching: isSubagentsFetching } = useQuery({
    queryKey: ["subagents", source_entity],
    queryFn: () => getLeanSubagents(source_entity),
    refetchOnWindowFocus: false,
    enabled: source_entity.includes("___member"),
  });

  const loading = isSubagentsFetching;

  const createQuotationMutation = useMutation({
    mutationFn: ({ payload }: { payload: QuotationRequestRQ }) => createQuotationV2({ payload }),
  });

  const rawSessionId = createQuotationMutation.data?.data?.session_id;

  // Handle create quotation request success
  useEffect(() => {
    if (!createQuotationMutation.isSuccess) return;
    toast.success(
      "Quotation request sent successfully. Please wait while we are processing your request.",
    );
    if (!rawSessionId) return;
    setSessionId(rawSessionId);
    setQuotationSession(null);
    createQuotationMutation.reset();
  }, [createQuotationMutation.isSuccess, rawSessionId, createQuotationMutation.reset]);

  // Handle quotation request error
  useEffect(() => {
    if (!createQuotationMutation.isError) return;
    toast.error("Quotation request sent failed. Please try again.");
    createQuotationMutation.reset();
  }, [createQuotationMutation.isError, createQuotationMutation.reset]);

  const { data: quotationRequestData } = useQuery({
    queryKey: ["quotation-request-polling", sessionId],
    queryFn: () => pollQuotationRequest(sessionId),
    enabled: !!sessionId && !["COMPLETED", "FAILED"].includes(quotationSession?.status ?? ""),
    refetchInterval: (data) => {
      if (data?.data?.status === "COMPLETED" || data?.data?.status === "FAILED") {
        return false;
      }
      return 3000;
    },
  });

  const sessionStatus = quotationRequestData?.data?.status;
  const generationProgress = quotationRequestData?.data?.generation_progress ?? 0;
  const generationProgressMessage = quotationRequestData?.data?.generation_progress_message ?? "";
  const errorMessage = quotationRequestData?.data?.error_message ?? "";

  const destinations = quotationSession?.quotation_request?.destinations ?? [];

  const excludeServiceMutation = useMutation({
    mutationFn: ({ session_id, service_id }: { session_id: string; service_id: string }) =>
      excludeService(session_id, service_id),
  });

  const reIncludeServiceMutation = useMutation({
    mutationFn: ({ session_id, service_id }: { session_id: string; service_id: string }) =>
      reIncludeService(session_id, service_id),
  });

  // Handle exclude service success
  useEffect(() => {
    if (!excludeServiceMutation.isSuccess) return;
    if (!excludeServiceMutation.data?.data?.service_id) return;

    toast.success("Service excluded successfully.");

    setQuotationSession((p) => {
      const qRequest = _.cloneDeep(p);
      if (!qRequest) return p;

      qRequest.quotation_request.excluded_service_uids.push(
        excludeServiceMutation.data.data.service_id,
      );
      return qRequest;
    });
    excludeServiceMutation.reset();
  }, [
    excludeServiceMutation.isSuccess,
    excludeServiceMutation.reset,
    excludeServiceMutation?.data?.data?.service_id,
  ]);

  // Handle re-include service success
  useEffect(() => {
    if (!reIncludeServiceMutation.isSuccess) return;

    toast.success("Service re-included successfully.");
    setQuotationSession((p) => {
      const qRequest = _.cloneDeep(p);
      if (!qRequest) return p;

      qRequest.quotation_request.excluded_service_uids =
        qRequest.quotation_request.excluded_service_uids.filter(
          (u) => u !== reIncludeServiceMutation.data.data.service_id,
        );
      return qRequest;
    });
    reIncludeServiceMutation.reset();
  }, [
    reIncludeServiceMutation.isSuccess,
    reIncludeServiceMutation.reset,
    reIncludeServiceMutation?.data?.data?.service_id,
  ]);

  const excludedServiceUids = new Set(
    quotationSession?.quotation_request?.excluded_service_uids ?? [],
  );

  // Handle quotation request preview creation.
  // If the session is completed, we will show the destinations.
  // If the session is failed, we will show the error message.
  useEffect(() => {
    if (!sessionStatus) return;
    if (sessionStatus !== "COMPLETED") return;

    toast.success("Quotation request preview successfully created.");
    setQuotationSession(quotationRequestData?.data);
  }, [sessionStatus, quotationRequestData?.data]);

  useEffect(() => {
    if (sessionStatus !== "FAILED") return;

    if (!!errorMessage) {
      toast.error(errorMessage, { autoClose: 10000 });
    } else {
      toast.error("Quotation request preview creation failed. Please try again.");
    }
    setSessionId("");
    setQuotationSession(null);
  }, [sessionStatus, errorMessage]);

  const subagentOptions: {
    label: string;
    value: string;
    name: string;
    email: string;
    legal_title: string;
  }[] = [];
  if (subagents?.data?.results) {
    for (let i = 0; i < subagents.data.results.length; i++) {
      const sub = subagents.data.results[i];
      if (!sub) continue;

      subagentOptions.push({
        label: `${sub.name} - ${sub.legal_title} - ${sub.email}`,
        name: sub.name,
        value: `${sub.id}___subagent`,
        email: sub.email,
        legal_title: sub.legal_title,
      });
    }
    sortByKey(subagentOptions, "label");
  }

  return (
    <Formik
      initialValues={createQuotationRequestRQ(source_entity, target_entity, initialMessage)}
      validationSchema={quotationRequestRQValidationSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={(values) => createQuotationMutation.mutate({ payload: values })}
    >
      {({ values, errors, submitForm }) => {
        return loading ? (
          <Loader />
        ) : (
          <Fragment>
            <div className="Modal__card__body overflow-y-hidden">
              {!sessionStatus ? (
                <div className="overflow-auto">
                  <h6>Please provide some general information about your trip.</h6>
                  <InitialForm
                    errors={errors}
                    subagentOptions={subagentOptions}
                    source_entity={source_entity}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1fr] xl:grid-cols-[1fr_2fr] gap-3 overflow-y-hidden">
                  <div className="overflow-auto p-3 rounded-md border border-gray-200">
                    <h6>Please provide some general information about your trip.</h6>
                    <InitialForm
                      errors={errors}
                      subagentOptions={subagentOptions}
                      source_entity={source_entity}
                    />
                  </div>
                  {sessionStatus === "PENDING" ? (
                    <QuotationRequestLoader
                      generationProgress={generationProgress}
                      generationProgressMessage={generationProgressMessage}
                    />
                  ) : (
                    <div className="grid gap-3 overflow-y-auto p-3 rounded-md border border-gray-200 auto-rows-max">
                      <div className="grid rounded-md shadow-md p-3">
                        <table>
                          <thead>
                            <tr>
                              <th className="text-left pb-1!">Start Date</th>
                              <th className="text-left pb-1!">Nights</th>
                              <th className="text-left pb-1!">Days</th>
                              <th className="text-left pb-1!">End Date</th>
                              <th className="text-left pb-1!">Adults</th>
                              <th className="text-left pb-1!">Children</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="text-left">
                                <span className="bg-gray-200 p-1 rounded-md">
                                  {!!tripStartDate
                                    ? DateTime.fromISO(tripStartDate).toLocaleString(
                                        DateTime.DATE_FULL,
                                      )
                                    : "N/A"}
                                </span>
                              </td>
                              <td className="text-left">
                                <span className="bg-gray-200 p-1 rounded-md">{nights}</span>
                              </td>
                              <td className="text-left">
                                <span className="bg-gray-200 p-1 rounded-md">{nights + 1}</span>
                              </td>
                              <td className="text-left">
                                <span className="bg-gray-200 p-1 rounded-md">{endDate}</span>
                              </td>
                              <td className="text-left">
                                <span className="bg-gray-200 p-1 rounded-md">{adults}</span>
                              </td>
                              <td className="text-left">
                                <span className="bg-gray-200 p-1 rounded-md">{children}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {destinations.map((dest, idx) => {
                        const serviceRequirements =
                          quotationSession?.quotation_request?.service_requirements?.[
                            dest.order.toString()
                          ] ?? [];
                        const activities = serviceRequirements.filter(
                          (a) => a.service_type === "ACT",
                        );
                        const coordinators = serviceRequirements.filter(
                          (a) => a.service_type === "COO",
                        );
                        const transfers = serviceRequirements.filter(
                          (a) => a.service_type === "TRF",
                        );
                        const accommodation = serviceRequirements.filter(
                          (a) => a.service_type === "ACC",
                        );

                        let tripLeg: TripLeg | null = null;
                        if (idx + 1 < destinations.length) {
                          const nextDest = destinations[idx + 1];
                          const key = `${dest.order}__${nextDest.order}`;
                          tripLeg = quotationSession?.quotation_request?.legs?.[key] ?? null;
                        }

                        return (
                          <Fragment key={`destination-${idx}-${dest.id}`}>
                            <QuotationDestination
                              destination={dest}
                              activities={activities}
                              coordinators={coordinators}
                              accommodation={accommodation}
                              transfers={transfers}
                              excludedServiceUids={excludedServiceUids}
                              excludeSrv={(uid: string) =>
                                excludeServiceMutation.mutate({
                                  session_id: sessionId,
                                  service_id: uid,
                                })
                              }
                              includeSrv={(uid: string) =>
                                reIncludeServiceMutation.mutate({
                                  session_id: sessionId,
                                  service_id: uid,
                                })
                              }
                            />
                            {!!tripLeg && (
                              <QuotationTripLeg
                                tripLeg={tripLeg}
                                destinations={destinations}
                                excludedServiceUids={excludedServiceUids}
                                excludeTripLeg={() =>
                                  excludeServiceMutation.mutate({
                                    session_id: sessionId,
                                    service_id: tripLeg.uid,
                                  })
                                }
                                includeTripLeg={() =>
                                  reIncludeServiceMutation.mutate({
                                    session_id: sessionId,
                                    service_id: tripLeg.uid,
                                  })
                                }
                              />
                            )}
                          </Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="Modal__card__actions grid grid-flow-col auto-cols-max justify-end">
              <BackButton onBack={onBack} />
              {!!values.message && (
                <button className="Button" type="button" data-brand={brand} onClick={submitForm}>
                  Generate Quotation Request
                </button>
              )}
              {quotationRequestData?.data.status === "COMPLETED" && (
                <button
                  className="Button"
                  type="button"
                  data-brand={brand}
                  onClick={() => finalizeQuotationRequest(sessionId)}
                >
                  Submit Quotation Request
                </button>
              )}
            </div>
          </Fragment>
        );
      }}
    </Formik>
  );
}
